/**
 * TimelineZoomSlider Component
 *
 * Zoom control slider with zoom in/out icons.
 * Adjusts timeline width (pixels per week).
 */

import React from 'react'
import { Box, Button, Paper, Slider, useMediaQuery, useTheme } from '@mui/material'
import * as Icons from '@mui/icons-material'
import { useTimelineStaticContext } from '../../context/timeline-static-context'
import { TimelineToggleWithPopper } from './timeline-toggle-with-popper'
import { CONTROLS_BREAKPOINT } from './timeline-controls-constants'
import { TpControlTooltip } from '../../../../../components/tp-control-tooltip'

const SLIDER_TRACK_THICKNESS = 3
const SLIDER_RAIL_THICKNESS = 3
const SLIDER_THUMB_SIZE = 12
const SLIDER_LENGTH = 200
const SLIDER_PAPER_THICKNESS = 24 // Match toggle button height

/**
 * TimelineZoomSlider - Zoom control slider
 *
 * Features:
 * - Dynamic min/max bounds based on container width
 * - Proportional scroll preservation during zoom
 * - Disabled state while initializing
 */
export const TimelineZoomSlider = () => {
  const theme = useTheme()
  const isExpanded = useMediaQuery(theme.breakpoints.up(CONTROLS_BREAKPOINT))
  const {
    weekContainerWidth,
    handleZoomChange,
    minWeekWidth,
    maxWeekWidth,
  } = useTimelineStaticContext()

  if (isExpanded) {
    return (
      <TpControlTooltip title="Zoom" offsetBottom={-8}>
        <Box
          sx={{
            width: SLIDER_LENGTH,
            height: SLIDER_PAPER_THICKNESS,
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            bgcolor: 'background.paper',
            px: 0,
            borderRadius: 1,
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          }}
        >
          <Button
            variant='text'
            onClick={() => handleZoomChange(null, Math.max(minWeekWidth, weekContainerWidth - 4))}
            sx={{
              minWidth: SLIDER_PAPER_THICKNESS,
              width: SLIDER_PAPER_THICKNESS,
              minHeight: SLIDER_PAPER_THICKNESS,
              height: SLIDER_PAPER_THICKNESS,
            }}
          >
            <Icons.Remove sx={{ fontSize: 16, color: theme.palette.text.secondary }} />
          </Button>
          <Slider
            value={weekContainerWidth}
            step={4}
            min={minWeekWidth}
            max={maxWeekWidth}
            onChange={handleZoomChange}
            size="small"
            disabled={weekContainerWidth === null}
            sx={{
              flex: 1,
              '& .MuiSlider-thumb': {
                width: SLIDER_THUMB_SIZE,
                height: SLIDER_THUMB_SIZE,
              },
              '& .MuiSlider-track': {
                height: SLIDER_TRACK_THICKNESS,
              },
              '& .MuiSlider-rail': {
                height: SLIDER_RAIL_THICKNESS,
              }
            }}
          />
          <Button
            variant='text'
            onClick={() => handleZoomChange(null, Math.min(maxWeekWidth, weekContainerWidth + 4))}
            sx={{
              minWidth: SLIDER_PAPER_THICKNESS,
              width: SLIDER_PAPER_THICKNESS,
              minHeight: SLIDER_PAPER_THICKNESS,
              height: SLIDER_PAPER_THICKNESS,
            }}
          >
            <Icons.Add sx={{ fontSize: 16, color: theme.palette.text.secondary }} />
          </Button>
          </Box>
      </TpControlTooltip>
    )
  }

  return (
    <TimelineToggleWithPopper
      title="Zoom"
      icon={<Icons.Expand fontSize='small' sx={{ transform: 'rotate(90deg)' }} />}
      tooltipOffsetBottom={SLIDER_LENGTH}
    >
      <Paper elevation={1}>
        <Box sx={{
          width: SLIDER_PAPER_THICKNESS * 1.25,
          height: SLIDER_LENGTH,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          py: 0,
          rowGap: 1,
          flexDirection: 'column',
        }}>
          <Button
            variant='text'
            onClick={() => handleZoomChange(null, Math.min(maxWeekWidth, weekContainerWidth + 4))}
            sx={{
              minWidth: SLIDER_PAPER_THICKNESS,
              width: SLIDER_PAPER_THICKNESS,
              minHeight: SLIDER_PAPER_THICKNESS,
              height: SLIDER_PAPER_THICKNESS,
            }}
          >
            <Icons.Add sx={{ fontSize: 16, color: theme.palette.text.secondary }} />
          </Button>
          <Slider
            orientation='vertical'
            value={weekContainerWidth}
            step={4}
            min={minWeekWidth}
            max={maxWeekWidth}
            onChange={handleZoomChange}
            size="small"
            disabled={weekContainerWidth === null}
            sx={{
              flex: 1,
              height: SLIDER_LENGTH,
              '& .MuiSlider-thumb': {
                width: SLIDER_THUMB_SIZE,
                height: SLIDER_THUMB_SIZE,
              },
              '& .MuiSlider-track': {
                width: SLIDER_TRACK_THICKNESS,
              },
              '& .MuiSlider-rail': {
                width: SLIDER_RAIL_THICKNESS,
              }
            }}
          />
          <Button
            variant='text'
            onClick={() => handleZoomChange(null, Math.max(minWeekWidth, weekContainerWidth - 4))}
            sx={{
              minWidth: SLIDER_PAPER_THICKNESS,
              width: SLIDER_PAPER_THICKNESS,
              minHeight: SLIDER_PAPER_THICKNESS,
              height: SLIDER_PAPER_THICKNESS,
            }}
          >
            <Icons.Remove sx={{ fontSize: 16, color: theme.palette.text.secondary }} />
          </Button>
        </Box>
      </Paper>
    </TimelineToggleWithPopper>
  )
}
